import React, { useState, useRef } from "react";
import axios from "axios";
import "./CreateBeautyPost.css";

const CreateBeautyPost = ({ onClose, onPostCreated }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    postType: "makeup_tutorial",
    hashtags: [],
    difficulty: "intermediate",
    duration: 0,
    tools: [],
    products: [],
    tags: {
      skinTypes: [],
      tones: [],
      techniques: []
    },
    allowComments: true,
    allowSharing: true
  });

  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("content");
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const postTypes = [
    "makeup_tutorial",
    "skincare_routine",
    "product_review",
    "before_after",
    "hair_styling",
    "nail_art",
    "makeup_look",
    "skincare_tips",
    "inspiration",
    "product_haul",
    "recommendation"
  ];

  const skinTypes = ["dry", "oily", "combination", "sensitive", "normal"];
  const tones = ["fair", "light", "medium", "tan", "deep"];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData({
        ...formData,
        [name]: checked
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleTagChange = (tagType, value) => {
    const tags = formData.tags[tagType];
    if (tags.includes(value)) {
      setFormData({
        ...formData,
        tags: {
          ...formData.tags,
          [tagType]: tags.filter((t) => t !== value)
        }
      });
    } else {
      setFormData({
        ...formData,
        tags: {
          ...formData.tags,
          [tagType]: [...tags, value]
        }
      });
    }
  };

  const handleHashtagInput = (e) => {
    if (e.key === "Enter") {
      const hashtag = e.target.value.trim();
      if (hashtag && !formData.hashtags.includes(hashtag)) {
        setFormData({
          ...formData,
          hashtags: [...formData.hashtags, hashtag]
        });
        e.target.value = "";
      }
    }
  };

  const removeHashtag = (hashtag) => {
    setFormData({
      ...formData,
      hashtags: formData.hashtags.filter((h) => h !== hashtag)
    });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setImages([...images, ...files]);
  };

  const handleVideoChange = (e) => {
    const files = Array.from(e.target.files);
    setVideos([...videos, ...files]);
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const removeVideo = (index) => {
    setVideos(videos.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.description.trim()) {
      setError("Título e descrição são obrigatórios");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const form = new FormData();

      // Adicionar dados de texto
      form.append("title", formData.title);
      form.append("description", formData.description);
      form.append("postType", formData.postType);
      form.append("difficulty", formData.difficulty);
      form.append("duration", formData.duration);
      form.append("allowComments", formData.allowComments);
      form.append("allowSharing", formData.allowSharing);
      form.append("hashtags", JSON.stringify(formData.hashtags));
      form.append("tags", JSON.stringify(formData.tags));
      form.append("products", JSON.stringify(formData.products));
      form.append("tools", JSON.stringify(formData.tools));

      // Adicionar imagens
      images.forEach((image) => {
        form.append("images", image);
      });

      // Adicionar vídeos
      videos.forEach((video) => {
        form.append("videos", video);
      });

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/beauty/post`,
        form,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );

      onPostCreated(response.data.data);
    } catch (err) {
      setError(
        err.response?.data?.message || "Erro ao criar post de beleza"
      );
      console.error("Erro:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="create-beauty-post-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Criar Post de Beleza</h3>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal-tabs">
          <button
            className={`tab-btn ${activeTab === "content" ? "active" : ""}`}
            onClick={() => setActiveTab("content")}
          >
            Conteúdo
          </button>
          <button
            className={`tab-btn ${activeTab === "media" ? "active" : ""}`}
            onClick={() => setActiveTab("media")}
          >
            Mídia
          </button>
          <button
            className={`tab-btn ${activeTab === "details" ? "active" : ""}`}
            onClick={() => setActiveTab("details")}
          >
            Detalhes
          </button>
        </div>

        <form onSubmit={handleSubmit} className="create-post-form">
          {/* TAB CONTEÚDO */}
          {activeTab === "content" && (
            <div className="tab-content">
              <div className="form-group">
                <label>Título *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Título do seu post..."
                  maxLength={200}
                />
                <span className="char-count">
                  {formData.title.length}/200
                </span>
              </div>

              <div className="form-group">
                <label>Descrição *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Descreva seu tutorial, look ou dica de beleza..."
                  maxLength={5000}
                  rows={6}
                />
                <span className="char-count">
                  {formData.description.length}/5000
                </span>
              </div>

              <div className="form-group">
                <label>Tipo de Post</label>
                <select
                  name="postType"
                  value={formData.postType}
                  onChange={handleInputChange}
                >
                  {postTypes.map((type) => (
                    <option key={type} value={type}>
                      {type.replace(/_/g, " ")}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Hashtags</label>
                <input
                  type="text"
                  placeholder="Digite e pressione Enter para adicionar..."
                  onKeyPress={handleHashtagInput}
                />
                <div className="hashtags-list">
                  {formData.hashtags.map((hashtag) => (
                    <span key={hashtag} className="hashtag">
                      {hashtag}
                      <button
                        type="button"
                        onClick={() => removeHashtag(hashtag)}
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB MÍDIA */}
          {activeTab === "media" && (
            <div className="tab-content">
              <div className="form-group">
                <label>Imagens</label>
                <div className="upload-area">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                  />
                  <button
                    type="button"
                    className="upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    📷 Selecionar Imagens
                  </button>
                  <p>ou arraste imagens aqui</p>
                </div>

                {images.length > 0 && (
                  <div className="media-preview">
                    <h4>Imagens Selecionadas ({images.length})</h4>
                    <div className="preview-grid">
                      {images.map((image, index) => (
                        <div key={index} className="preview-item">
                          <img
                            src={URL.createObjectURL(image)}
                            alt={`preview-${index}`}
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="remove-btn"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Vídeos</label>
                <div className="upload-area">
                  <input
                    ref={videoInputRef}
                    type="file"
                    multiple
                    accept="video/*"
                    onChange={handleVideoChange}
                    style={{ display: "none" }}
                  />
                  <button
                    type="button"
                    className="upload-btn"
                    onClick={() => videoInputRef.current?.click()}
                  >
                    🎥 Selecionar Vídeos
                  </button>
                  <p>ou arraste vídeos aqui</p>
                </div>

                {videos.length > 0 && (
                  <div className="media-preview">
                    <h4>Vídeos Selecionados ({videos.length})</h4>
                    <div className="preview-list">
                      {videos.map((video, index) => (
                        <div key={index} className="video-item">
                          <span>📹 {video.name}</span>
                          <button
                            type="button"
                            onClick={() => removeVideo(index)}
                            className="remove-btn"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB DETALHES */}
          {activeTab === "details" && (
            <div className="tab-content">
              <div className="form-group">
                <label>Nível de Dificuldade</label>
                <select
                  name="difficulty"
                  value={formData.difficulty}
                  onChange={handleInputChange}
                >
                  <option value="beginner">Iniciante</option>
                  <option value="intermediate">Intermediário</option>
                  <option value="advanced">Avançado</option>
                </select>
              </div>

              <div className="form-group">
                <label>Duração (minutos)</label>
                <input
                  type="number"
                  name="duration"
                  value={formData.duration}
                  onChange={handleInputChange}
                  min="0"
                />
              </div>

              <div className="form-group">
                <label>Tipos de Pele</label>
                <div className="checkbox-group">
                  {skinTypes.map((type) => (
                    <label key={type} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.tags.skinTypes.includes(type)}
                        onChange={() => handleTagChange("skinTypes", type)}
                      />
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Tons de Pele</label>
                <div className="checkbox-group">
                  {tones.map((tone) => (
                    <label key={tone} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.tags.tones.includes(tone)}
                        onChange={() => handleTagChange("tones", tone)}
                      />
                      {tone.charAt(0).toUpperCase() + tone.slice(1)}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="allowComments"
                    checked={formData.allowComments}
                    onChange={handleInputChange}
                  />
                  Permitir comentários
                </label>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="allowSharing"
                    checked={formData.allowSharing}
                    onChange={handleInputChange}
                  />
                  Permitir compartilhamento
                </label>
              </div>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          <div className="modal-footer">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={loading}
            >
              {loading ? "Postando..." : "Postar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateBeautyPost;
